import React, { useState } from 'react';
import {
  X, Sparkles, Building2, Database, BookOpen, FileText, FileSpreadsheet, ShieldCheck,
  CheckCircle2, ArrowRight, Lock, Zap, Clock, Smartphone, ChevronRight, Brain,
  Sliders, Settings, FolderOpen, Layers, Terminal, Compass, Check, HelpCircle
} from 'lucide-react';

interface CorporateOfficeInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenOffice?: () => void;
  language: 'uk' | 'en' | 'pl';
}

export default function CorporateOfficeInfoModal({
  isOpen,
  onClose,
  onOpenOffice,
  language
}: CorporateOfficeInfoModalProps) {
  if (!isOpen) return null;

  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const [activeTab, setActiveTab] = useState<'architecture' | 'memory' | 'actions' | 'setup' | 'pricing'>('architecture');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      <div 
        className="relative w-full max-w-5xl max-h-[92vh] bg-slate-950 border border-violet-500/40 rounded-3xl shadow-2xl flex flex-col overflow-hidden text-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="px-5 sm:px-6 py-4 bg-gradient-to-r from-violet-950/90 via-zinc-900 to-indigo-950/90 border-b border-zinc-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30 text-white shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base sm:text-lg font-black tracking-tight text-white">
                  {isPl ? 'Korporacyjne Biuro Cyfrowe' : isEn ? 'Corporate Digital Office' : 'Корпоративний Цифровий Офіс'}
                </h2>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-violet-500/20 text-violet-300 border border-violet-500/30">
                  SYSTEM ARCHITECTURE & GUIDE 2026
                </span>
              </div>
              <p className="text-xs text-zinc-400">
                {isPl 
                  ? 'Jak działa pamięć, proces generowania dokumentów i gdzie konfigurować parametry' 
                  : isEn 
                  ? 'Detailed mechanics of memory, execution pipelines, and exact configuration points' 
                  : 'Повна інженерна архітектура пам\'яті, алгоритм дій ШІ та практичний посібник з налаштування'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors cursor-pointer border border-zinc-800 shrink-0"
            title="Закрити"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="px-4 sm:px-6 py-2.5 bg-zinc-900/80 border-b border-zinc-800 flex items-center gap-2 overflow-x-auto no-scrollbar shrink-0 text-xs font-semibold">
          <button
            onClick={() => setActiveTab('architecture')}
            className={`px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              activeTab === 'architecture'
                ? 'bg-violet-600 text-white shadow-md shadow-violet-500/25'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>{isPl ? '1. Architektura i Proces' : isEn ? '1. Architecture & Pipeline' : '1. Архітектура та Конвеєр'}</span>
          </button>

          <button
            onClick={() => setActiveTab('memory')}
            className={`px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              activeTab === 'memory'
                ? 'bg-violet-600 text-white shadow-md shadow-violet-500/25'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <Brain className="w-3.5 h-3.5 text-violet-300" />
            <span>{isPl ? '2. Pamięć i Samorozwój' : isEn ? '2. Memory & Self-Learning' : '2. Пам\'ять та Самонавчання'}</span>
          </button>

          <button
            onClick={() => setActiveTab('actions')}
            className={`px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              activeTab === 'actions'
                ? 'bg-violet-600 text-white shadow-md shadow-violet-500/25'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <Zap className="w-3.5 h-3.5 text-amber-300" />
            <span>{isPl ? '3. Działania i Pliki DSTU' : isEn ? '3. Actions & Documents' : '3. Виробничі Дії та ДСТУ'}</span>
          </button>

          <button
            onClick={() => setActiveTab('setup')}
            className={`px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              activeTab === 'setup'
                ? 'bg-violet-600 text-white shadow-md shadow-violet-500/25'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <Compass className="w-3.5 h-3.5 text-cyan-300" />
            <span>{isPl ? '4. Gdzie i co Konfigurować' : isEn ? '4. Configuration Guide' : '4. Де і що налаштовувати'}</span>
          </button>

          <button
            onClick={() => setActiveTab('pricing')}
            className={`px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              activeTab === 'pricing'
                ? 'bg-violet-600 text-white shadow-md shadow-violet-500/25'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            <span>{isPl ? '5. Pakiet B2B i Warunki' : isEn ? '5. B2B Package & Trial' : '5. Пакет B2B та Умови'}</span>
          </button>
        </div>

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-6">

          {/* TAB 1: ARCHITECTURE & PIPELINE */}
          {activeTab === 'architecture' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="bg-gradient-to-br from-violet-900/30 via-zinc-900/90 to-indigo-900/20 border border-violet-500/30 rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-violet-500/20 text-violet-300 border border-violet-500/40">
                    MULTI-TENANT SANDBOX & CONTEXT COMPILATION
                  </span>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-white">
                  {isPl
                    ? 'Czym różni się Korporacyjne Biuro od zwykłego chatbota?'
                    : isEn
                    ? 'How Corporate Digital Office Differs from a Generic Chatbot'
                    : 'Чим Корпоративний Цифровий Офіс відрізняється від звичайного чат-бота?'}
                </h3>
                <p className="text-xs sm:text-sm text-zinc-300 leading-relaxed">
                  {isPl
                    ? 'Zwykły chatbot nie zna struktury Twojej firmy, zapomina wcześniejsze ustalenia po zamknięciu karty i generuje niesformatowany tekst. Korporacyjne Biuro Cyfrowe to zintegrowany silnik operacyjny z odizolowaną przestrzenią dyskową (Sandbox), buforem pamięci instytucjonalnej, podłączeniem do oryginalnych papierów firmowych oraz kompilatorami do plików Word (.docx) i Excel (.xlsx).'
                    : isEn
                    ? 'Generic chatbots do not know your corporate DNA, forget conversations once you refresh, and spit out plain markdown text. The Corporate Digital Office is a full operational engine with an isolated sandbox filesystem, institutional memory neocortex, direct letterhead overlays, and compilers into binary Word (.docx) and formula-driven Excel (.xlsx) files.'
                    : 'Звичайний чат-бот не знає вашої компанії, забуває розмову після закриття вкладки і видає звичайний неструктурований текст. Корпоративний Цифровий Офіс — це повний операційний комплекс із персональним захищеним каталогом на сервері (Sandbox), довічною пам\'яттю підприємства, інтеграцією з оригінальними фірмовими бланками установи та власними компіляторами документів Word (.docx) і Excel (.xlsx).'}
                </p>
              </div>

              {/* 5-Stage Processing Pipeline */}
              <div className="space-y-3">
                <h4 className="text-xs font-black uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-violet-400" />
                  <span>{isPl ? '5-etapowy proces realizacji każdego zadania (Execution Pipeline)' : isEn ? '5-Stage Task Execution Pipeline' : '5-етапний наскрізний конвеєр виконання кожного завдання'}</span>
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
                  <div className="p-4 rounded-xl bg-zinc-900/70 border border-zinc-800 space-y-2">
                    <div className="w-6 h-6 rounded-lg bg-violet-600/30 text-violet-300 flex items-center justify-center font-mono font-bold text-xs">
                      1
                    </div>
                    <p className="font-bold text-white">Вхідне завдання</p>
                    <p className="text-zinc-400 text-[11px] leading-relaxed">
                      Співробітник формулює запит або прикріплює договір, акт чи регламент (PDF/Word/Excel/зображення).
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-zinc-900/70 border border-zinc-800 space-y-2">
                    <div className="w-6 h-6 rounded-lg bg-indigo-600/30 text-indigo-300 flex items-center justify-center font-mono font-bold text-xs">
                      2
                    </div>
                    <p className="font-bold text-white">Ін'єкція контексту</p>
                    <p className="text-zinc-400 text-[11px] leading-relaxed">
                      Сервер автоматично витягує з Sandbox: реквізити фірми (паспорт), WIKI (базу знань), правила AGENTS.md та активні бланки.
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-zinc-900/70 border border-zinc-800 space-y-2">
                    <div className="w-6 h-6 rounded-lg bg-blue-600/30 text-blue-300 flex items-center justify-center font-mono font-bold text-xs">
                      3
                    </div>
                    <p className="font-bold text-white">Smart Caching</p>
                    <p className="text-zinc-400 text-[11px] leading-relaxed">
                      Google Smart Context Caching оптимізує 32k+ токенів корпоративного контексту, скорочуючи час відповіді до 1.5–3 секунд.
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-zinc-900/70 border border-zinc-800 space-y-2">
                    <div className="w-6 h-6 rounded-lg bg-emerald-600/30 text-emerald-300 flex items-center justify-center font-mono font-bold text-xs">
                      4
                    </div>
                    <p className="font-bold text-white">Генерація артефактів</p>
                    <p className="text-zinc-400 text-[11px] leading-relaxed">
                      ШІ створює відповідь, формує блоки <code>artifact:file</code> (документи) та <code>artifact:memory</code> (нові правила).
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-zinc-900/70 border border-zinc-800 space-y-2">
                    <div className="w-6 h-6 rounded-lg bg-amber-600/30 text-amber-300 flex items-center justify-center font-mono font-bold text-xs">
                      5
                    </div>
                    <p className="font-bold text-white">Компіляція файлів</p>
                    <p className="text-zinc-400 text-[11px] leading-relaxed">
                      Серверні скрипти зшивають текст із фірмовим бланком за ДСТУ 4163:2020 або збирають робочий Excel з живими формулами.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: MEMORY & SELF-LEARNING */}
          {activeTab === 'memory' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="bg-gradient-to-br from-purple-950/40 via-zinc-900 to-indigo-950/30 border border-purple-500/40 rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-purple-400" />
                  <span className="text-xs font-bold text-purple-300 uppercase tracking-wider">
                    {isPl ? 'ARCHITEKTURA PAMIĘCI I PROTOKÓŁ SAMOROZWOJU' : isEn ? 'MEMORY ARCHITECTURE & SELF-LEARNING PROTOCOL' : 'АРХІТЕКТУРА ПАМ\'ЯТІ ТА ПРОТОКОЛ САМОРОЗВИТКУ'}
                  </span>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-white">
                  {isPl
                    ? '4 poziomy pamięci korporacyjnej firmy'
                    : isEn
                    ? '4 Tiers of Enterprise Institutional Memory'
                    : '4 рівні корпоративної пам\'яті вашого підприємства'}
                </h3>
                <p className="text-xs text-zinc-300 leading-relaxed">
                  {isPl
                    ? 'System nie korzysta z jednego niestabilnego okna kontekstu. Podobnie jak ludzki mózg, wykorzystuje dwupoziomowy model CLS (Complementary Learning Systems): szybki bufor epizodyczny oraz trwałą skompilowaną pamięć neocortex.'
                    : isEn
                    ? 'The platform does not rely on a brittle single context window. Mimicking human cognitive architecture (Complementary Learning Systems), it combines a fast episodic buffer with a permanent compiled neocortex.'
                    : 'Система не покладається на єдине крихке вікно чату. Подібно до людського мозку (модель Complementary Learning Systems), пам\'ять офісу розділена на швидкий епізодичний буфер та довготривалий Неокортекс.'}
                </p>
              </div>

              {/* 4 Tiers Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                {/* Tier 1 */}
                <div className="p-4 rounded-2xl bg-zinc-900/60 border border-zinc-800 space-y-2">
                  <div className="flex items-center gap-2 font-bold text-white">
                    <span className="w-6 h-6 rounded-lg bg-violet-600/30 text-violet-300 flex items-center justify-center font-mono">1</span>
                    <span>Епізодична пам'ять (Гіпокамп / Чат)</span>
                  </div>
                  <p className="text-zinc-400 leading-relaxed">
                    Зберігається в базі даних SQLite (таблиця <code>enterprise_brain_messages</code>). ШІ тримає в активній пам'яті останні 10 повідомлень поточної сесії та прикріплені файли. Використовується для оперативної роботи над конкретним наказом чи договором.
                  </p>
                  <span className="inline-block px-2 py-0.5 rounded bg-zinc-800 text-[10px] text-zinc-400 font-mono">
                    Ізольована в межах конкретного діалогу
                  </span>
                </div>

                {/* Tier 2 */}
                <div className="p-4 rounded-2xl bg-zinc-900/60 border border-violet-500/40 space-y-2">
                  <div className="flex items-center gap-2 font-bold text-white">
                    <span className="w-6 h-6 rounded-lg bg-purple-600/30 text-purple-300 flex items-center justify-center font-mono">2</span>
                    <span>Довготривалий Неокортекс (<code>PROJECT_WIKI.md</code>)</span>
                  </div>
                  <p className="text-zinc-400 leading-relaxed">
                    Структурована скомпільована база знань фірми на диску. <strong>Ніколи не забувається</strong> і підключається до системного промпту при кожному новому зверненні (до 100k символів). Містить регламенти, контрагентів, тарифи та історію рішень.
                  </p>
                  <span className="inline-block px-2 py-0.5 rounded bg-purple-950/80 text-[10px] text-purple-300 font-mono border border-purple-500/30">
                    Спільна для всіх сесій та всіх агентів
                  </span>
                </div>

                {/* Tier 3 */}
                <div className="p-4 rounded-2xl bg-zinc-900/60 border border-zinc-800 space-y-2">
                  <div className="flex items-center gap-2 font-bold text-white">
                    <span className="w-6 h-6 rounded-lg bg-blue-600/30 text-blue-300 flex items-center justify-center font-mono">3</span>
                    <span>Цифровий паспорт (<code>CLIENT_PROFILE.md</code>)</span>
                  </div>
                  <p className="text-zinc-400 leading-relaxed">
                    Постійні юридичні та банківські реквізити установи: код ЄДРПОУ, точна юридична назва, посада та ПІБ директора, банківський рахунок IBAN/МФО, система оподаткування. ШІ підставляє їх у шапки та підписи автоматично.
                  </p>
                  <span className="inline-block px-2 py-0.5 rounded bg-zinc-800 text-[10px] text-zinc-400 font-mono">
                    Автоматичне заповнення реквізитів
                  </span>
                </div>

                {/* Tier 4 */}
                <div className="p-4 rounded-2xl bg-zinc-900/60 border border-zinc-800 space-y-2">
                  <div className="flex items-center gap-2 font-bold text-white">
                    <span className="w-6 h-6 rounded-lg bg-emerald-600/30 text-emerald-300 flex items-center justify-center font-mono">4</span>
                    <span>Залізні правила та кодекс (<code>AGENTS.md</code>)</span>
                  </div>
                  <p className="text-zinc-400 leading-relaxed">
                    Незмінні директиви якості: суворе дотримання ДСТУ 4163:2020 для Word, формування діючих математичних формул для Excel, нульова терпимість до вигадок і посилання виключно на нормативну базу України.
                  </p>
                  <span className="inline-block px-2 py-0.5 rounded bg-zinc-800 text-[10px] text-zinc-400 font-mono">
                    Гарантія юридичної відповідності
                  </span>
                </div>
              </div>

              {/* 3 Active Self-Learning Methods */}
              <div className="p-5 rounded-2xl bg-gradient-to-br from-violet-950/60 via-zinc-900 to-indigo-950/50 border border-violet-500/40 space-y-3">
                <h4 className="text-xs font-black uppercase tracking-wider text-white flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span>{isPl ? 'Jak działa samorozwój: 3 sposoby utrwalania nowej wiedzy' : isEn ? 'How Self-Learning Works: 3 Ways to Persist Knowledge' : 'Як працює саморозвиток: 3 активні способи закріплення нових знань'}</span>
                </h4>

                <div className="space-y-3 text-xs">
                  <div className="p-3 rounded-xl bg-zinc-950/70 border border-zinc-800 space-y-1">
                    <p className="font-bold text-violet-300">
                      ⚡ Спосіб 1: Пряма команда в чаті (In-Chat Learning)
                    </p>
                    <p className="text-zinc-300">
                      Просто напишіть у повідомленні: <em>«Запам'ятай нову інструкцію: усі закупівлі понад 50 000 грн погоджуються фінансовим директором»</em> або натисніть кнопку-підказку <strong>«🧠 Запам'ятати правило»</strong>. ШІ згенерує артефакт пам'яті, бекенд автоматично оновить WIKI, а на екрані з'явиться бейдж підтвердження.
                    </p>
                  </div>

                  <div className="p-3 rounded-xl bg-zinc-950/70 border border-zinc-800 space-y-1">
                    <p className="font-bold text-indigo-300">
                      🌙 Спосіб 2: Кнопка «Консолідувати WIKI» (Штучний сон / Reflection)
                    </p>
                    <p className="text-zinc-300">
                      Після завершення довгої сесії обговорення натисніть кнопку <strong>«🧠 Консолідувати WIKI»</strong> у верхньому правому куті чату. Фоновий агент-аналітик вивчить весь транскрипт сесії, відсіє розмовний шум і перенесе всі нові домовленості, тарифи та рішення в хроніку WIKI.
                    </p>
                  </div>

                  <div className="p-3 rounded-xl bg-zinc-950/70 border border-zinc-800 space-y-1">
                    <p className="font-bold text-cyan-300">
                      ✍️ Спосіб 3: Прямий ручний контроль (Human-in-the-Loop)
                    </p>
                    <p className="text-zinc-300">
                      У будь-який момент відкрийте вкладку <strong>«База знань (WIKI)»</strong> або <strong>«Паспорт фірми»</strong> вгорі екрана. Ви можете вручну відредагувати текст, додати цілий розділ правил, змінити телефон чи номер рахунку та натиснути кнопку <strong>«Зберегти зміни»</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: ACTIONS & DSTU */}
          {activeTab === 'actions' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="bg-gradient-to-br from-blue-950/40 via-zinc-900 to-indigo-950/30 border border-blue-500/40 rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-400" />
                  <span className="text-xs font-bold text-blue-300 uppercase tracking-wider">
                    {isPl ? 'STANDARDY DOKUMENTACJI PRAWNEJ I FINANSOWEJ' : isEn ? 'OFFICIAL LEGAL & FINANCIAL DOCUMENT STANDARDS' : 'СТАНДАРТИ ОФІЦІЙНОГО ДІЛОВОДСТВА ТА ФІНАНСІВ'}
                  </span>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-white">
                  {isPl
                    ? 'Jak system generuje gotowe pliki Word i Excel'
                    : isEn
                    ? 'How the System Produces Signature-Ready Word & Excel Files'
                    : 'Як система формує готові до підпису файли Word (.docx) та Excel (.xlsx)'}
                </h3>
                <p className="text-xs text-zinc-300 leading-relaxed">
                  {isPl
                    ? 'Zamiast surowego tekstu do skopiowania, system kompiluje profesjonalne pliki gotowe do wysyłki do ministerstw, partnerów i banków.'
                    : isEn
                    ? 'Instead of raw unformatted chat text, the system compiles enterprise-grade binary documents ready for submission to government agencies and banks.'
                    : 'Замість звичайного тексту для копіювання, система збирає повноцінні файли, готові для затвердження керівником, реєстрації в канцелярії та подання до міністерств і банків.'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                {/* Word DSTU */}
                <div className="p-5 rounded-2xl bg-zinc-900/60 border border-blue-500/30 space-y-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-600/20 text-blue-400 flex items-center justify-center font-bold">
                    <FileText className="w-5 h-5" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Документи Word за ДСТУ 4163:2020</h4>
                  <p className="text-zinc-400 leading-relaxed">
                    Компільовані файли (.docx) строго відповідають Національному стандарту України ДСТУ 4163:2020:
                  </p>
                  <ul className="space-y-1.5 text-zinc-300 pl-4 list-disc text-[11px]">
                    <li>Поля сторінки: ліве 30 мм, праве 10 мм, верхнє/нижнє 20 мм;</li>
                    <li>Шрифт Times New Roman 14 pt, міжрядковий інтервал 1.15;</li>
                    <li>Реквізити 01–32 (дата, індекс, заголовок «Про...», розпорядча частина «НАКАЗУЮ:»);</li>
                    <li><strong>Фірмові бланки:</strong> якщо завантажено офіційний бланк компанії (шапка ДКАУ/НЦУВКЗ тощо), сервер автоматично зливає текст без задвоєння шапки.</li>
                  </ul>
                </div>

                {/* Excel Formulas */}
                <div className="p-5 rounded-2xl bg-zinc-900/60 border border-emerald-500/30 space-y-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-600/20 text-emerald-400 flex items-center justify-center font-bold">
                    <FileSpreadsheet className="w-5 h-5" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Таблиці Excel з живими формулами</h4>
                  <p className="text-zinc-400 leading-relaxed">
                    Замість мертвого тексту серверний компілятор <code>convert_report_to_formula_xlsx.py</code> створює справжні файли .xlsx:
                  </p>
                  <ul className="space-y-1.5 text-zinc-300 pl-4 list-disc text-[11px]">
                    <li>Активні математичні формули (<code>=SUM()</code>, <code>=AVERAGE()</code>, податок 20% ПДВ);</li>
                    <li>При зміні будь-якого вхідного числа підсумкові суми перераховуються автоматично;</li>
                    <li>Автоматичне форматування фінансових колонок (грн, %, дати);</li>
                    <li>Стильні заголовки, вирівнювання та готовність до друку А4.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: SETUP GUIDE */}
          {activeTab === 'setup' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="bg-gradient-to-br from-cyan-950/40 via-zinc-900 to-indigo-950/30 border border-cyan-500/40 rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Compass className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs font-bold text-cyan-300 uppercase tracking-wider">
                    {isPl ? 'PRAKTYCZNY PRZEWODNIK KONFIGURACJI DLA KIEROWNIKA' : isEn ? 'EXECUTIVE STEP-BY-STEP SETUP GUIDE' : 'ПРАКТИЧНИЙ ПОСІБНИК З НАЛАШТУВАННЯ ДЛЯ КЕРІВНИКА'}
                  </span>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-white">
                  {isPl
                    ? 'Gdzie i co skonfigurować, aby biuro działało idealnie?'
                    : isEn
                    ? 'Exact Configuration Points to Optimize Your Office'
                    : 'Де і що налаштувати, щоб Цифровий Офіс працював ідеально?'}
                </h3>
                <p className="text-xs text-zinc-300 leading-relaxed">
                  {isPl
                    ? 'Wszystkie ustawienia zajmują 5 minut i nie wymagają umiejętności programowania. Oto mapa elementów systemu:'
                    : isEn
                    ? 'Complete configuration takes under 5 minutes without any coding. Here is the operational roadmap:'
                    : 'Повне налаштування займає до 5 хвилин і не потребує технічних знань. Ось покрокова карта налаштувань:'}
                </p>
              </div>

              {/* Step-by-Step Configuration Map */}
              <div className="space-y-3 text-xs">
                {/* Step 1 */}
                <div className="p-4 rounded-xl bg-zinc-900/70 border border-zinc-800 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-violet-600/30 text-violet-300 flex items-center justify-center font-bold shrink-0 font-mono">
                    1
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-white">Вкладка «Паспорт фірми» (Profile)</p>
                    <p className="text-zinc-400 text-[11px] leading-relaxed">
                      <strong>Де знаходиться:</strong> кнопка «Паспорт фірми» у верхній панелі офісу або вкладка «Статутні дані» в Enterprise Dashboard.<br/>
                      <strong>Що налаштувати:</strong> код ЄДРПОУ, офіційну назву, посаду та ПІБ керівника («Начальник центру», «Директор»), рахунок IBAN та МФО банку. Це гарантує правильність усіх підписів і реквізитів.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="p-4 rounded-xl bg-zinc-900/70 border border-zinc-800 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-indigo-600/30 text-indigo-300 flex items-center justify-center font-bold shrink-0 font-mono">
                    2
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-white">Розділ «Фірмові бланки» (Letterheads)</p>
                    <p className="text-zinc-400 text-[11px] leading-relaxed">
                      <strong>Де знаходиться:</strong> Кабінет підприємства (Enterprise Dashboard) → вкладка «Фірмові бланки».<br/>
                      <strong>Що налаштувати:</strong> завантажте оригінальні шаблони .docx наказів та вихідних листів із логотипом установи. Сервер буде автоматично комбінувати текст наказів з оригінальним бланком установи.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="p-4 rounded-xl bg-zinc-900/70 border border-zinc-800 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-600/30 text-blue-300 flex items-center justify-center font-bold shrink-0 font-mono">
                    3
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-white">Вкладка «База знань (WIKI)»</p>
                    <p className="text-zinc-400 text-[11px] leading-relaxed">
                      <strong>Де знаходиться:</strong> кнопка «База знань (WIKI)» у верхній панелі офісу.<br/>
                      <strong>Що налаштувати:</strong> структура відділів, діючі прайси, тарифи, реєстр постійних контрагентів та внутрішні регламенти погодження договорів.
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="p-4 rounded-xl bg-zinc-900/70 border border-zinc-800 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-emerald-600/30 text-emerald-300 flex items-center justify-center font-bold shrink-0 font-mono">
                    4
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-white">Вкладка «Сховище» та завантаження документів</p>
                    <p className="text-zinc-400 text-[11px] leading-relaxed">
                      <strong>Де знаходиться:</strong> кнопка «Сховище» або скріпка 📎 у полі вводу чату.<br/>
                      <strong>Що завантажити:</strong> завантажуйте внутрішні положення про відділи, контракти на аудит, акти та статути. ШІ парсить їхній зміст та враховує при підготовці юридичних відповідей.
                    </p>
                  </div>
                </div>

                {/* Step 5 */}
                <div className="p-4 rounded-xl bg-zinc-900/70 border border-zinc-800 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-amber-600/30 text-amber-300 flex items-center justify-center font-bold shrink-0 font-mono">
                    5
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-white">Створення кастомних агентів для відділів</p>
                    <p className="text-zinc-400 text-[11px] leading-relaxed">
                      <strong>Де знаходиться:</strong> Кабінет підприємства → кнопка «+ Створити кастомного агента».<br/>
                      <strong>Що налаштувати:</strong> створіть персонального помічника під відділ кадрів, тендерний відділ чи склад із власною вузькою базою знань (RAG) та системною інструкцією.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: B2B COMMERCIAL & TRIAL */}
          {activeTab === 'pricing' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="p-6 rounded-2xl bg-gradient-to-br from-indigo-950/50 via-zinc-900 to-purple-950/40 border-2 border-indigo-500/40 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-500/20 text-indigo-300 border border-indigo-500/40">
                      {isPl ? 'PAKIET ALL-IN-ONE B2B' : isEn ? 'ALL-IN-ONE B2B ENTERPRISE' : 'КОМПЛЕКСНИЙ ПАКЕТ B2B «ВСЕ ВКЛЮЧЕНО»'}
                    </span>
                    <span className="text-xs font-bold text-white">
                      {isPl ? 'Pojedyncza subskrypcja na całe biuro' : isEn ? 'Single subscription for the entire office' : 'Єдина підписка на весь простір'}
                    </span>
                  </div>
                  <span className="text-[11px] font-mono font-bold text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/30">
                    {isPl ? 'Bez ukrytych dopłat' : isEn ? 'Zero hidden fees' : 'Без розділення та доплат'}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-zinc-950/60 border border-zinc-800">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span className="text-zinc-300">
                      <strong className="text-white">AI-Офіс & WIKI:</strong> робочий чат, збереження регламентів та пам'яті фірми в ізольованому Sandbox без ліміту на кількість запитів.
                    </span>
                  </div>
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-zinc-950/60 border border-zinc-800">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span className="text-zinc-300">
                      <strong className="text-white">Документи Word (.docx) за ДСТУ:</strong> накази, договори, довідки з миттєвим завантаженням та Excel з живими формулами.
                    </span>
                  </div>
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-zinc-950/60 border border-zinc-800">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span className="text-zinc-300">
                      <strong className="text-white">40+ Галузевих Модулів:</strong> доступ до Універсального Консультанта (464 професії), ситуаційних карт та галузевих хабів.
                    </span>
                  </div>
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-zinc-950/60 border border-zinc-800">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span className="text-zinc-300">
                      <strong className="text-white">Повний Кабінет & Первинні Акти:</strong> реквізити ЄДРПОУ, фірмові бланки, щомісячні офіційні Акти від ФОП Аксьонов С.М. (100% IP Банах А.С.).
                    </span>
                  </div>
                </div>
              </div>

              {/* Free Trial Banner */}
              <div className="p-4 rounded-2xl bg-gradient-to-r from-violet-950/70 via-slate-900 to-purple-950/60 border border-violet-500/40 space-y-2 text-xs">
                <div className="flex items-center gap-2 font-bold text-amber-300">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span>{isPl ? '🔥 3 DNI DARMOWEGO OKRESU PRÓBNEGO (FREE TRIAL)' : isEn ? '🔥 3 DAYS FREE TRIAL FOR ENTERPRISES' : '🔥 3 ДНІ БЕЗКОШТОВНОГО ТЕСТОВОГО ПЕРІОДУ (FREE TRIAL)'}</span>
                </div>
                <p className="text-zinc-300 text-[11px] leading-relaxed">
                  {isPl
                    ? 'Przetestuj pełną funkcjonalność biura cyfrowego bez zobowiązań. Po okresie próbnym — bezpieczna płatność przez Monobank lub na rachunek bankowy z wystawieniem oficjalnego aktu wykonania usług dla księgowości.'
                    : isEn
                    ? 'Test the full functionality of the corporate digital office with zero risk. Official service acts under DSTU 4163:2020 issued upon payment for accounting departments.'
                    : 'Оцініть усі можливості корпоративного цифрового офісу без фінансових зобов\'язань. За потреби надаються офіційні розрахункові первинні акти за ДСТУ 4163:2020 для бухгалтерії підприємства.'}
                </p>
                <div className="pt-2 border-t border-zinc-800 text-[10px] text-zinc-400 flex flex-wrap items-center justify-between gap-2 font-mono">
                  <div>
                    Виконавець та оператор платформи: <span className="text-zinc-200 font-semibold">ФОП Аксьонов С.М.</span> (РНОКПП: 2648220818) • АТ «УНІВЕРСАЛ БАНК»
                  </div>
                  <div>
                    100% IP & Розробник: <span className="text-zinc-200 font-semibold">Банах А.С.</span>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer Actions */}
        <div className="px-5 sm:px-6 py-3.5 bg-zinc-900 border-t border-zinc-800 flex items-center justify-between shrink-0">
          <div className="text-xs text-zinc-400 flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <span className="hidden sm:inline">{isPl ? 'Bezpieczne środowisko Sandbox' : isEn ? 'Isolated sandbox environment' : 'Ізольований захищений простір Sandbox'}</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-750 text-zinc-300 text-xs font-semibold transition-colors cursor-pointer border border-zinc-700/60"
            >
              {isPl ? 'Zamknij' : isEn ? 'Close' : 'Зрозуміло'}
            </button>

            {onOpenOffice && (
              <button
                onClick={() => {
                  onClose();
                  onOpenOffice();
                }}
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white text-xs font-bold flex items-center gap-2 transition-all cursor-pointer shadow-lg shadow-violet-500/25"
              >
                <span>{isPl ? 'Przejdź do Biura' : isEn ? 'Open Digital Office' : 'Увійти в Цифровий Офіс'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
